import { useMemo } from 'react';

import { useQuery } from 'react-query';

import processingActivitiesApi from 'config/api/processingActivities/processingActivities';
import type { AssetProcessingActivitiesMetrics } from 'config/api/processingActivities/processingActivities.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import {
  catchHttpStatuses,
  getUnavailableStatus,
  HTTP_UNAVAILABLE_STATUSES,
  unwrapAvailableQueryData,
} from 'pages/ProcessingActivities/_shared/queryUnavailable';

import {
  parseAssetProcessingActivitiesMetricsFromDetail,
  parseProcessingActivityDetailViewModel,
} from './processingActivityDetail.parsers';

type UseProcessingActivityDetailOptions = {
  organizationId?: number;
  processingActivityId?: number;
};

const useProcessingActivityDetail = ({ organizationId, processingActivityId }: UseProcessingActivityDetailOptions) => {
  const detailQuery = useQuery(
    [QUERY_KEYS.PROCESSING_ACTIVITIES_GET_DETAIL, organizationId, processingActivityId],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await processingActivitiesApi.getDetail(organizationId as number, processingActivityId as number);
        return data;
      }, HTTP_UNAVAILABLE_STATUSES),
    {
      enabled: Boolean(organizationId && processingActivityId),
    },
  );

  const { data: detailQueryData, isError: isDetailError, isLoading, refetch } = detailQuery;
  const unavailableStatus = getUnavailableStatus(detailQueryData);
  const detail = unwrapAvailableQueryData(detailQueryData);
  const viewModel = useMemo(() => (detail ? parseProcessingActivityDetailViewModel(detail) : undefined), [detail]);

  const assetProcessingsMetricsQuery = useQuery(
    [QUERY_KEYS.PROCESSING_ACTIVITIES_ASSET_PROCESSINGS_METRICS, organizationId, processingActivityId],
    async () => {
      const { data } = await processingActivitiesApi.getAssetProcessingActivitiesMetrics(
        organizationId as number,
        processingActivityId as number,
      );
      return data;
    },
    {
      enabled: Boolean(organizationId && processingActivityId && detail),
      retry: false,
    },
  );

  const metricsFromDetail = detail ? parseAssetProcessingActivitiesMetricsFromDetail(detail) : null;
  const metricsFromEndpoint = assetProcessingsMetricsQuery.data;
  const metrics: AssetProcessingActivitiesMetrics | undefined = metricsFromEndpoint ?? metricsFromDetail ?? undefined;

  const isForbidden = unavailableStatus === 403;
  const isNotFound = unavailableStatus === 404;
  const isError = isDetailError;

  return {
    detail,
    isError,
    isForbidden,
    isLoading,
    isNotFound,
    metrics,
    refetch,
    viewModel,
  };
};

export default useProcessingActivityDetail;
